import axios, { AxiosError, AxiosRequestConfig } from "axios";
import { toast } from "react-toastify";
import { useMemo } from "react";
export interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  globalErrorHandler?: boolean;
}

interface APIErrorResponse {
  message: string;
  errors?: any;
  data?: any;
  details?: any;
}

export const BASE_URL = process.env.REACT_APP_API_KEY;

export const BASE_URL_NEXUS = process.env.REACT_APP_API_KEY_NEXUS;

export const BASE_URL_APP_VERSION = `${process.env.REACT_APP_API_KEY}/api/${process.env.REACT_APP_VERSION}`;

function handleError(error: AxiosError<APIErrorResponse>) {
  const config = error.config as CustomAxiosRequestConfig;
  if (config.globalErrorHandler === false) {
    return;
  }

  if (error.response) {
    switch (error?.response?.status) {
      case 400:
        if (error.response.data?.details[0].message) {
          toast.warning(` ${error.response.data?.details[0].message}`);
        } else if (error.response.data?.message) {
          toast.warning(
            `${error.response.data?.message}
         `
          );
        } else {
          toast.warning(
            `${error.message}
         `
          );
        }
        break;
      case 401:
        if (error.response.data?.details[0]) {
          toast.error(` ${error.response.data?.details[0].message}`);
        } else {
          toast.error(
            "شما به این بخش دسترسی ندارید. " + error.response.data?.message
          );
        }

        break;
      case 403:
        if (error.response.data?.details[0]) {
          toast.error(` ${error.response.data?.details[0].message}`);
        } else {
          toast.error("مشکلی پیش آمده است. " + error.response.data?.message);
        }
        break;
      case 404:
        if (error.response.data?.details[0]) {
          toast.error(` ${error.response.data?.details[0].message}`);
        } else {
          toast.error(
            "خطای 404: یافت نشد. " + error.response.data?.message ||
              (error?.name as string)
          );
        }
        break;
      case 500:
        toast.error("ارتباط با سرور به درستی برقرار نشد");
        break;
      case 502:
        toast.info("سامانه در حال بروزرسانی است. شکیبا باشید");
        break;
      default:
        toast.error(
          `Error ${error.response?.status}: ${error.response.statusText}`
        );
    }
    return;
  }
  if (error.request) {
    return;
  }
  if (error.message) {
    toast.error("error . message");
    return;
  }
  if (error.code === "ECONNABORTED") {
    toast.error("تایم اوت.");
  }
  if (error.code === "ERR_NETWORK") {
    toast.error("لطفا اتصال به اینترنت خود را بررسی نمایید.");
    return;
  } else {
    console.error(error.message || "An unknown error occurred.");
  }

  console.error("Global Error Handler:", error);
}

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    handleError(error);
    return Promise.reject(error);
  }
);

const axiosInstance = axios.create({
  baseURL: BASE_URL_APP_VERSION,
  timeout: 60000,
  headers: {
    "Content-Type": "application/json",
    "Accept-Language": "en-US,en",
    Authorization: localStorage.getItem("access_token")
      ? `Bearer ${localStorage.getItem("access_token")}`
      : null,
  },
  withCredentials: true,
});

export const CREDITOR_CREDITOR_MANAGEMENT = "creditor/creditor-management/";

export const CREDITOR_DISABLE_USER = (creditor_user_uid: string) =>
  `/creditor/disable-creditor-user/${creditor_user_uid}/`;

export const ADMIN_STAFF_UID = (staff_uid: string) =>
  `/admin/staff/${staff_uid}/`;

export const CREDITOR_ENABLE_USER = (creditor_user_uid: string) =>
  `/creditor/enable-creditor-user/${creditor_user_uid}`;

export const CREDITOR_USERS = (creditor_uid: string) =>
  `/creditor/${creditor_uid}/users/?page=1&page_size=100`;

export const axiosPrivate = axios.create({
  baseURL: BASE_URL_APP_VERSION,
  headers: {
    "Content-Type": "application/json",
    "Accept-Language": "en-US,en",
  },
  withCredentials: true,
  timeout: 600000,
});

export const axiosPrivateNexus = axios.create({
  baseURL: BASE_URL_NEXUS,
  headers: {
    "Content-Type": "application/json",
    "Accept-Language": "en-US,en",
  },
  withCredentials: true,
  timeout: 600000,
});

export const useAxiosInstance = (isForm?: boolean) => {
  return useMemo(() => {
    return axios.create({
      baseURL: BASE_URL_APP_VERSION,
      headers: {
        "Content-Type": isForm ? "multipart/form-data" : "application/json",
        "Accept-Language": "en-US,en",
        Authorization: localStorage.getItem("access_token")
          ? `Bearer ${localStorage.getItem("access_token")}`
          : null,
      },
      withCredentials: true,
      timeout: 600000,
    });
  }, [localStorage.getItem("access_token")]);
};

export const axiosPrivateHasToken = axios.create({
  baseURL: BASE_URL_APP_VERSION,
  headers: {
    "Content-Type": "application/json",
    "Accept-Language": "en-US,en",
    Authorization: `Bearer ${localStorage.getItem("access_token")}`,
  },
  withCredentials: true,
  timeout: 600000,
});

export const axiosPrivateformdata = axios.create({
  baseURL: BASE_URL_APP_VERSION,
  headers: {
    "Content-Type": "multipart/form-data",
    "Accept-Language": "en-US,en",
    Authorization: localStorage.getItem("access_token")
      ? `Bearer ${localStorage.getItem("access_token")}`
      : null,
  },
  withCredentials: true,
  timeout: 600000,
});

const instances = [
  axiosPrivate,
  axiosPrivateNexus,
  axiosPrivateHasToken,
  axiosPrivateformdata,
];

localStorage.setItem(
  "permissions",
  JSON.stringify(["edit_store_info", "edit_store_license", "other_permission"])
);

instances.forEach((instance) => {
  instance.interceptors.request.use(
    (config) => {
      const method = (config.method || "").toUpperCase();
      if (["POST", "PUT", "PATCH", "DELETE"].includes(method)) {
        const viewOnly = localStorage.getItem("view_only") === "true";
        if (viewOnly) {
          toast.error("شما دسترسی ویرایش ندارید");
          return Promise.reject(new Error("View-only access"));
        }
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      handleError(error);
      return Promise.reject(error);
    }
  );
});

axiosInstance.interceptors.request.use(
  (config) => {
    const method = (config.method || "").toUpperCase();
    if (["POST", "PUT", "PATCH", "DELETE"].includes(method)) {
      const viewOnly = localStorage.getItem("view_only") === "true";
      if (viewOnly) {
        toast.error("شما دسترسی ویرایش ندارید");
        return Promise.reject(new Error("View-only access"));
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

export default axiosInstance;
